import {BRAND} from '#/lib/brand'
import {
  type ImpersonationLinks,
  type SessionAccount,
} from '#/state/session/types'

/**
 * Base URL of THIS brand's SSO login/handoff broker. cooey talks to delta
 * (chat.cooey.club/api); eagledrive talks to its OWN pds-pro
 * (admin.eagledrive.live) so its login is fully isolated from cooey. Both
 * speak the identical 5-field redeem contract.
 */
const API_BASE = BRAND.apiBase

/**
 * Full-page URL that starts this brand's SSO login: the broker runs the
 * Authentik OIDC login (cooey: Discord; eagledrive: FB/TikTok on the Eagle
 * Drive brand) and redirects back to `<BRAND.siteUrl>/#h=<token>` with a
 * handoff token this module redeems. Most users never learn their
 * Authentik-managed atproto password, so this SSO path is the default sign-in
 * wherever `BRAND.sso` is set.
 */
export const COOEY_LOGIN_URL = `${API_BASE}${BRAND.ssoLoginUrl}`

/** Kick off the Cooey SSO login (web: top-level navigation to the IdP). */
export function startCooeyLogin() {
  if (typeof window !== 'undefined') {
    window.location.href = COOEY_LOGIN_URL
  }
}

type BrokeredSession = {
  accessJwt: string
  refreshJwt: string
  did: string
  handle: string
  pds: string
  /** Admin "assume user": load this session EPHEMERALLY (tab-only, never
   * persisted) behind an "Acting as" banner. Absent/false for a normal login. */
  impersonation?: boolean
  /** Console jump-links the banner offers while impersonating. Empty/absent for
   * a normal login or a surface that isn't enabled on the deployment. */
  studioUrl?: string
  brandUrl?: string
}

/**
 * One-click login from Cooey chat. Chat opens `x.cooey.club/#h=<token>` with a
 * short-lived, single-use handoff token that delta minted for the signed-in
 * chat user; we exchange it (`POST /auth/atproto/redeem`) for that user's own
 * atproto session and hand back a `SessionAccount` the app can `resumeSession`.
 *
 * Web only, and best-effort: returns undefined when there is no token or the
 * exchange fails, so the app falls back to its normal boot. The token is
 * stripped from the URL before the network call so it never lingers in history.
 */
export async function consumeCooeyHandoff(): Promise<
  | {
      account: SessionAccount
      impersonation: boolean
      links: ImpersonationLinks
    }
  | undefined
> {
  if (typeof window === 'undefined') return undefined

  const match = (window.location.hash || '').match(/[#&]h=([^&]+)/)
  if (!match) return undefined
  const token = decodeURIComponent(match[1])

  // Strip the token from the URL immediately, before we even use it.
  try {
    const url = new URL(window.location.href)
    url.hash = ''
    window.history.replaceState(null, '', url.toString())
  } catch {}

  try {
    const res = await fetch(`${API_BASE}${BRAND.ssoRedeemPath}`, {
      method: 'POST',
      headers: {'content-type': 'application/json'},
      body: JSON.stringify({token}),
    })
    if (!res.ok) return undefined
    const s = (await res.json()) as BrokeredSession

    /*
     * Only service/did/handle are required by the account schema; resumeSession
     * validates the JWTs against the PDS and refreshes in the rest (email etc.).
     */
    return {
      account: {
        service: s.pds,
        did: s.did,
        handle: s.handle,
        accessJwt: s.accessJwt,
        refreshJwt: s.refreshJwt,
        active: true,
        pdsUrl: s.pds,
        isSelfHosted: true,
      },
      impersonation: !!s.impersonation,
      links: {
        studio: s.studioUrl || undefined,
        brand: s.brandUrl || undefined,
      },
    }
  } catch {
    return undefined
  }
}
